/**
 * FvbProceedToCheckout is a custom element that displays a sticky checkout bar
 * at the bottom of the cart page when the default checkout section scrolls out of view.
 *
 * Visibility is controlled via IntersectionObserver watching #cart-summary.
 * Price is kept in sync with .cart__summary-total-price via MutationObserver.
 * Both observers are re-initialized after cart AJAX updates.
 */
if (!customElements.get('fvb-proceed-to-checkout')) {
  class FvbProceedToCheckout extends HTMLElement {
    connectedCallback() {
      this._init();
      this._watchCartUpdates();
      this._setupCheckout();
    }

    disconnectedCallback() {
      this._visibilityObserver?.disconnect();
      this._priceObserver?.disconnect();
      this._cartObserver?.disconnect();
    }

    _init() {
      this._setupVisibility();
      this._setupPriceSync();
    }

    _setupVisibility() {
      this._visibilityObserver?.disconnect();

      const buttons = [...document.querySelectorAll('button[name="checkout"]')]
        .filter(btn => !btn.closest('fvb-proceed-to-checkout'));

      if (!buttons.length) return;

      const visible = new Set();

      this._visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) visible.add(e.target);
          else visible.delete(e.target);
        });
        this.classList.toggle('is-hidden', visible.size > 0);
      }, { threshold: 0, rootMargin: '-60px 0px -60px 0px' });

      buttons.forEach(btn => this._visibilityObserver.observe(btn));
    }

    _setupPriceSync() {
      this._priceObserver?.disconnect();
      const source = document.querySelector('.cart__summary-total-price');
      if (!source) return;

      // Sync immediately (covers the case where cart-items was re-rendered by AJAX)
      this._syncPrice(source);

      // Watch for any subsequent direct text changes to the element
      this._priceObserver = new MutationObserver(() => this._syncPrice(source));
      this._priceObserver.observe(source, { childList: true, characterData: true, subtree: true });
    }

    _syncPrice(source) {
      const priceEl = this.querySelector('.cart__sticky-checkout--price');
      if (priceEl) priceEl.textContent = source.textContent;
    }

    _setupCheckout() {
      const stickyBtn = this.querySelector('.cart__sticky-checkout--action button');
      if (!stickyBtn) return;

      stickyBtn.addEventListener('click', () => {
        const form = document.getElementById('cart');
        if (!form) return;

        // If terms checkbox exists and is unchecked: scroll it into view, then
        // call reportValidity() so the native browser message appears next to it.
        const terms = form.querySelector('#Terms-Conditions');
        if (terms && !terms.checked) {
          terms.closest('.cart__terms')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Delay matches scroll animation so the tooltip renders on-screen
          setTimeout(() => terms.reportValidity(), 400);
          return;
        }

        // Submit via the original button so its name="checkout" value is included
        // in the POST payload, which is what Shopify uses to redirect to checkout.
        const checkoutBtn = form.querySelector('button[name="checkout"]');
        form.requestSubmit(checkoutBtn ?? null);
      });
    }

    // Re-initialize after cart AJAX updates replace cart-items innerHTML
    _watchCartUpdates() {
      const cartItems = document.querySelector('cart-items');
      if (!cartItems) return;

      let raf;
      this._cartObserver = new MutationObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this._init());
      });
      this._cartObserver.observe(cartItems, { childList: true });
    }
  }

  customElements.define('fvb-proceed-to-checkout', FvbProceedToCheckout);
}
